import React from 'react';
import styled from '@emotion/styled/macro';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { H2Atom } from '@atoms/text-atoms/text-atoms';
import { LetterboxLinkAtom } from '@atoms/link-atoms/link-atoms';
import { SignUpFooterLinkProps } from './sign-up-footer-link.types';

const SignUpFooterLink: React.FC<SignUpFooterLinkProps> = ({
  translate = true,
  titleHeading = Translate('News and updates from', translate),
  titleName = Translate('The Ellen MacArthur Foundation', translate),
  backgroundImageSrc,
  ctaText = Translate('Click to subscribe', translate),
  ctaUrl,
  className,
}) => {
  if (ctaUrl && titleHeading && titleName) {
    return (
      <section
        data-testid="sign-up-footer"
        className={className}
        role="complementary"
      >
        <div className="content-container">
          {titleHeading && titleName && (
            <H2Atom>
              <div className="title-heading">{titleHeading}</div>
              <div className="title-name">{titleName}</div>
            </H2Atom>
          )}
          {ctaUrl && (
            <LetterboxLinkAtom
              url={ctaUrl}
              gtmId={GTM_ID.SIGN_UP_FOOTER_LINK}
              dataTestId="sign-up-footer-cta"
            >
              {ctaText}
            </LetterboxLinkAtom>
          )}
        </div>
      </section>
    );
  } else {
    console.warn(
      `Sign Up Footer Link called with missing props. CTA text: ${ctaText}, CTA url: ${ctaUrl}, title heading: ${titleHeading}, title name: ${titleName}, background image: ${backgroundImageSrc}`
    );
    return null;
  }
};

const StyledSignUpFooterLink = styled(SignUpFooterLink)`
  --component-height: 25.3rem;
  --text-width: 34.7rem;
  --link-max-width: 15.5rem;

  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: var(--component-height);
  background-color: ${vars.coreDarkGrey};
  background: ${({ backgroundImageSrc }) =>
    backgroundImageSrc ? `url(${backgroundImageSrc}) no-repeat center` : null};
  background-size: cover;

  .content-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;

    ${H2Atom} {
      padding: 0 0 1.6rem 0;
      .title-heading {
        ${vars.fontSize4}
        font-weight: ${vars.fontWeightRegular};
        letter-spacing: -0.04em;
        color: white;
        letter-spacing: 0;
        margin: 0 0 ${vars.spacingXs} 0;
        &:first-letter {
          text-transform: uppercase;
        }
      }
      .title-name {
        ${vars.fontSize6}
        font-weight: ${vars.fontWeightMedium};
        letter-spacing: -0.04em;
        color: white;
        padding-bottom: ${vars.spacingXs};
        &:first-letter {
          text-transform: uppercase;
        }
      }
    }

    ${LetterboxLinkAtom} {
      display: flex;
      align-items: center;
      justify-content: center;
      max-width: var(--link-max-width);
      height: 5rem;
      font-size: 14px;
      letter-spacing: -0.03em;
      border-radius: 4px;
      cursor: pointer;
      &:hover {
        background: white;
        color: ${vars.coreDarkGrey};
        cursor: pointer;
      }
    }
  }
`;

export default StyledSignUpFooterLink;
